import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from '../../core/auth'
import { isPreviewing } from '../../core/preview'
import { holds } from '../../core/caps'
import type { NtAttendance, NtBlock, NtCourse, NtDay, NtSelection, NtPlacement, NtProposal, NtRoster, NtSchedule, NtStudent, NtTerm } from '../../core/types'
import {
  useTerm, useCourses, useMyRosters, useStudents, useProposals, usePlacement, useVisits, useAwards,
  useSeminarProposals, useMyProposal, useMyAttendanceTaken, unplannedParts, useExemplars, useCourseExemplars,
  useTermAttendance, useAllRosters, useMySchedule, useTermSchedules, useMyStudentsSchedules, claimSections,
  useSelections, useMySelection, useApprovedCourses,
} from './model'
import { mergeSelections } from './selections'
import { classYearFromEmail, gradeFor } from '../../core/students'
import { buildChecklist, currentPhase, sessionLabel, type CheckGroup, type PhaseKey } from './checklist'
import { exceptions, notTaken, termDay, type ArtifactOf } from './attendanceLogic'

/**
 * NorseTerm's data, loaded once for the whole app.
 *
 * Two places need it at the same time — the NorseTerm screen and the
 * checklist under NorseTerm in the left rail — and two copies of every
 * listener would be twice the reads for the same answer. So it lives here,
 * above both.
 *
 * Every subscription is gated on whether this person may read it. That is not
 * tidiness: a listener the rules refuse can wedge the browser's local cache,
 * which is exactly what happened the first time a student opened this.
 */

export interface NorseTermRoles {
  teach: boolean; review: boolean; configure: boolean; run: boolean
  /** The office: attendance and where every student is. */
  attend: boolean; seminar: boolean; student: boolean
  /** May set other people's access (admin.grant): the NorseTerm staff page. */
  grant?: boolean
}

interface NorseTermData {
  enabled: boolean
  term: NtTerm
  loading: boolean
  roles: NorseTermRoles
  courses: NtCourse[] | null
  coursesErr: string | null
  myCourses: NtCourse[]
  rosters: NtRoster[]
  /** The teacher's classes, morning before afternoon: attendance is per day and block. */
  mySessions: Array<{ courseId: string; block: NtBlock | null; title: string; friday: boolean }>
  /** Attendance in for this teacher's courses, as `${courseId}_${day}`. */
  myAttendanceTaken: Set<string>
  students: NtStudent[] | null
  proposals: NtProposal[]
  placement: NtPlacement | null
  seminarProposals: NtProposal[]
  myProposal: NtProposal | null | undefined
  /** A student's own course selections: undefined = none yet, null = loading. */
  mySelection: NtSelection | null | undefined
  /** Every student's in-app course selections, for whoever runs placement or reviews. */
  selections: NtSelection[]
  /** The approved catalog, for a student choosing. Null while loading or for staff. */
  approvedCourses: NtCourse[] | null
  /** Office and admins: every course's attendance and every roster, for the reports. */
  termAttendance: NtAttendance[]
  allRosters: NtRoster[]
  /** A student's own week; undefined until placement reaches them. */
  mySchedule: NtSchedule | null | undefined
  /** Office/admin: everyone's. Teachers: their own students'. */
  schedules: NtSchedule[]
  /** studentId → the block they make their artifact in — who is where on Friday morning. */
  artifactOf: ArtifactOf
  /**
   * What is sitting in a queue that only this person can move: course
   * proposals to review, student projects ready for final approval, and
   * proposals waiting on them as seminar teacher.
   */
  waiting: {
    courses: number; projects: number; stalled: number; seminar: number
    /** During the week only: students absent today, and courses that have not taken today's attendance. */
    absentToday: number; notTakenToday: number
    total: number
  }
  /** The phase the calendar says it is. */
  phase: PhaseKey
  checklist: (phase: PhaseKey) => CheckGroup[]
  /** Called by About Norse Term, so "Read about Norse Term" ticks itself. */
  markInfoRead: () => void
  /**
   * The bell: things only this person can act on, each linking to the
   * action. A course or proposal sent back for revision, reviews waiting,
   * today's attendance. `n` is how many; the bell shows the sum.
   */
  alerts: Alert[]
}

export interface Alert { id: string; n: number; text: string; to: string }

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

// Per device, and allowed to fail: a private window simply never ticks it.
const INFO_KEY = 'nt.infoRead'
const readInfoFlag = () => { try { return localStorage.getItem(INFO_KEY) === '1' } catch { return false } }

const Ctx = createContext<NorseTermData | null>(null)

export function NorseTermProvider({ children }: { children: ReactNode }) {
  const { status, user, profile, student, can, isSeminarTeacher, canUseTool } = useAuth()
  const ready = status === 'ready' && canUseTool('norseterm')
  const staff = ready && !!profile
  const roles: NorseTermRoles = {
    teach: staff && can('norseterm.teach'),
    review: staff && can('norseterm.review'),
    configure: staff && can('norseterm.configure'),
    run: staff && can('norseterm.run'),
    attend: staff && can('norseterm.attend'),
    grant: staff && can('admin.grant'),
    seminar: staff && isSeminarTeacher,
    student: ready && !!student,
  }
  const email = (user?.email ?? '').toLowerCase()

  const { term, loading } = useTerm(ready)
  const tid = ready ? term.id : ''
  const sid = staff ? tid : ''
  const { courses, err } = useCourses(sid)
  const rosters = useMyRosters(roles.teach ? tid : '', user?.uid, email)
  const { students: imported } = useStudents(tid, roles.run)
  // In-app course selections, merged over any CSV import: placement and the
  // dashboard read one list either way.
  const selections = useSelections(tid, roles.run || roles.review || roles.attend)
  const students = useMemo(() => (imported === null && !selections.length ? null
    : mergeSelections(imported ?? [], selections)), [imported, selections])
  const mySelection = useMySelection(roles.student ? tid : '', email)
  const approvedAll = useApprovedCourses(tid, roles.student)
  // Credit recovery is placed by hand, never chosen, so it is not in the catalog.
  const approvedCourses = useMemo(() => approvedAll && approvedAll.filter((c) => !c.assignedOnly), [approvedAll])
  const proposals = useProposals(tid, roles.run || roles.review || roles.attend)
  const placement = usePlacement(tid, roles.run || roles.attend)
  const visits = useVisits(sid)
  const awards = useAwards(sid)
  const seminarProposals = useSeminarProposals(roles.seminar ? tid : '', email)
  const myProposal = useMyProposal(roles.student ? tid : '', email)
  const myAttendanceTaken = useMyAttendanceTaken(tid, rosters.map((r) => r.courseId))
  const exemplars = useExemplars(roles.configure)
  const courseExemplars = useCourseExemplars(roles.configure)
  const termAttendance = useTermAttendance(tid, roles.attend)
  const allRosters = useAllRosters(tid, roles.run || roles.attend)
  const mySchedule = useMySchedule(roles.student ? tid : '', email)
  const termSchedules = useTermSchedules(tid, roles.run || roles.review || roles.attend)
  const teacherSchedules = useMyStudentsSchedules(tid, user?.uid, roles.teach && !roles.run && !roles.review && !roles.attend)
  const schedules = roles.run || roles.review || roles.attend ? termSchedules : teacherSchedules
  const artifactOf: ArtifactOf = useMemo(() => new Map(schedules.map((x) => [x.studentId, x.artifactBlock])), [schedules])
  // The teacher's classes in the order of the day: attendance is taken per
  // day and block ("Tuesday morning"). Friday morning is artifact prep, so a
  // course is there only if someone makes their artifact in it.
  const mySessions = useMemo(() => [...rosters]
    .sort((a, b) => (a.block === 'PM' ? 1 : 0) - (b.block === 'PM' ? 1 : 0))
    .map((r) => ({ courseId: r.courseId, block: r.block, title: (courses ?? []).find((c) => c.id === r.courseId)?.title ?? 'Your course',
      friday: r.students.some((s) => artifactOf.get(s.studentId) === r.block) })), [rosters, courses, artifactOf])
  const myAttendanceDays = useMemo(() => new Set([0, 1, 2, 3].filter((d) =>
    mySessions.filter((x) => d !== 3 || x.friday).every((x) => myAttendanceTaken.has(`${x.courseId}_${d}`)))), [mySessions, myAttendanceTaken])

  const myCourses = useMemo(() => (courses ?? []).filter((c) => c.teacherUid === user?.uid), [courses, user?.uid])
  // A credit recovery section admin named this teacher for, before they had
  // signed in: take it up now, so rosters and attendance know it is theirs.
  const unclaimed = useMemo(() => (courses ?? []).filter((c) => c.assignedOnly && !c.teacherUid && c.teacherEmail === email),
    [courses, email])
  useEffect(() => {
    if (!roles.teach || !user?.uid || !unclaimed.length || isPreviewing()) return
    void claimSections(unclaimed, user.uid).catch(() => {})
  }, [roles.teach, user?.uid, unclaimed])

  // A district admin passes every capability check, so can('norseterm.teach')
  // is always true for them, but they only *teach* if they hold the Teacher
  // role. Having a course does not count: a test draft would otherwise give
  // an admin a "Your course" section they do not need.
  const teaches = !profile?.districtAdmin ? roles.teach
    : staff && holds(profile.caps, 'norseterm.teach')

  const [infoRead, setInfoRead] = useState(readInfoFlag)
  const markInfoRead = useCallback(() => {
    try { localStorage.setItem(INFO_KEY, '1') } catch { /* private window: fine */ }
    setInfoRead(true)
  }, [])

  const base = useMemo<Omit<NorseTermData, 'alerts'>>(() => {
    const myVisits = visits.filter((v) => v.visitorEmail === email)
    return {
      enabled: ready, term, loading, roles, courses, coursesErr: err, myCourses, rosters, mySessions, myAttendanceTaken,
      students, proposals, placement, seminarProposals, myProposal, mySelection, selections, approvedCourses, termAttendance, allRosters, mySchedule, schedules, artifactOf,
      phase: currentPhase(term), markInfoRead,
      waiting: (() => {
        const w = {
          courses: roles.review ? (courses ?? []).filter((c) => c.status === 'submitted').length : 0,
          projects: roles.review || roles.run ? proposals.filter((p) => p.status === 'checked').length : 0,
          // Still with a seminar teacher — admin/office may step in, so it is
          // shown, but it is not counted as theirs to do.
          stalled: roles.review || roles.run ? proposals.filter((p) => p.status === 'submitted').length : 0,
          seminar: roles.seminar ? seminarProposals.filter((p) => p.status === 'submitted').length : 0,
          ...(() => {
            // Alerts only while the week is actually on — an absence from last
            // Tuesday is a report, not something to act on now.
            const today = termDay(term)
            if (!roles.attend || today === null) return { absentToday: 0, notTakenToday: 0 }
            return {
              absentToday: exceptions(courses ?? [], allRosters, termAttendance, today, artifactOf).filter((r) => r.status === 'absent').length,
              notTakenToday: notTaken(courses ?? [], allRosters, termAttendance, today, artifactOf).length,
            }
          })(),
        }
        return { ...w, total: w.courses + w.projects + w.seminar + w.absentToday + w.notTakenToday }
      })(),
      checklist: (phase) => buildChecklist({
        term, roles: { ...roles, teach: teaches }, myCourses, courses: courses ?? [], myRosterCount: rosters.length, myAttendanceDays,
        mySessions, myAttendanceTaken,
        myVisits, visits, studentsImported: roles.run ? (students?.length ?? 0) : null, placement,
        mySelection: mySelection ?? undefined,
        myGrade: roles.student && email ? (() => { const y = classYearFromEmail(email); return y ? gradeFor(y) : undefined })() : undefined,
        proposals, seminarProposals, awards: awards.length, exemplars: exemplars.length, courseExemplars: courseExemplars.length,
        schoolAttendanceDays: roles.attend && allRosters.length
          ? new Set([0, 1, 2, 3].filter((d) => notTaken(courses ?? [], allRosters, termAttendance, d as NtDay, artifactOf).length === 0))
          : undefined,
        infoRead,
        mySchedule: mySchedule ?? undefined, schedules, myProposal: myProposal ?? undefined, unplannedParts: (c) => unplannedParts(c.plan ?? []),
      }, phase),
    }
    // roles is rebuilt each render from stable inputs; its fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, term, loading, courses, err, myCourses, rosters, students, proposals, placement,
      seminarProposals, myProposal, mySelection, selections, approvedCourses, myAttendanceDays, mySessions, myAttendanceTaken, visits, awards, exemplars, courseExemplars, email, teaches, termAttendance, allRosters, mySchedule, schedules, artifactOf,
      infoRead, markInfoRead,
      roles.teach, roles.review, roles.configure, roles.run, roles.attend, roles.seminar, roles.student])

  const value = useMemo<NorseTermData>(() => {
    const w = base.waiting
    const alerts: Alert[] = []
    // Sent back for revision: the one alert a teacher or student gets.
    if (base.roles.teach) {
      for (const c of myCourses.filter((x) => x.status === 'returned')) {
        alerts.push({ id: `returned_${c.id}`, n: 1,
          text: `“${c.title || 'Your course'}” was sent back with notes. Revise it and send it again.`,
          to: `/norseterm/mine?course=${c.id}` })
      }
    }
    if (base.roles.student && base.myProposal?.status === 'returned') {
      alerts.push({ id: 'proposal_returned', n: 1,
        text: 'Your independent project proposal was sent back with notes. Revise it and submit it again.', to: '/norseterm' })
    }
    if (w.courses) alerts.push({ id: 'courses', n: w.courses,
      text: `${plural(w.courses, 'teacher course proposal', 'teacher course proposals')} to verify`, to: '/norseterm/review' })
    if (w.projects) alerts.push({ id: 'projects', n: w.projects,
      text: `${plural(w.projects, 'independent project proposal', 'independent project proposals')} ready for your approval`, to: '/norseterm/projects' })
    if (w.seminar) alerts.push({ id: 'seminar', n: w.seminar,
      text: `${plural(w.seminar, 'seminar project proposal', 'seminar project proposals')} to check off`, to: '/norseterm/seminar' })
    if (w.absentToday) alerts.push({ id: 'absent', n: w.absentToday,
      text: `${plural(w.absentToday, 'student', 'students')} absent today`, to: '/norseterm/attendance' })
    if (w.notTakenToday) alerts.push({ id: 'notTaken', n: w.notTakenToday,
      text: `${plural(w.notTakenToday, 'course has', 'courses have')} not taken attendance today`, to: '/norseterm/attendance' })
    // A teacher's own attendance, on the day itself.
    const today = termDay(base.term)
    if (base.roles.teach && today !== null) {
      const due = mySessions.filter((x) => (today !== 3 || x.friday) && !myAttendanceTaken.has(`${x.courseId}_${today}`))
      if (due.length) alerts.push({ id: 'myAttendance', n: due.length,
        text: `Take attendance: ${due.map((x) => sessionLabel(today, x.block)).join(', ')}`,
        to: `/norseterm/attend?day=${today}&course=${due[0]!.courseId}` })
    }
    return { ...base, alerts }
  }, [base, myCourses, mySessions, myAttendanceTaken])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useNorseTerm(): NorseTermData {
  const v = useContext(Ctx)
  if (!v) throw new Error('useNorseTerm must be used inside <NorseTermProvider>')
  return v
}
